import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { Alert, Button, Spinner, Tab, Tabs } from "react-bootstrap";
import { FaUserCircle } from "react-icons/fa";
import { MdError } from "react-icons/md";
import { PiArrowBendUpLeft } from "react-icons/pi";
import { TbChecks } from "react-icons/tb";
import { TiVendorMicrosoft } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import { APIState, AuditObjectTypes } from "../../../_core/_model.ts";
import { GetUsers } from "../../../_core/_request.ts";
import { LabelConstants } from "../../../Constants.ts";
import { useAppContext } from "../../../context/AppContext.tsx";
import { AuthContext } from "../../../context/AuthContext.js";
import { defaultTheme } from "../../../styles/themes/defaultTheme";
import MandatoryStar from "../../../UIComponents/MandatoryStar.tsx";
import SavingIndicator from "../../../UIComponents/SavingIndicator.tsx";
import ComponentLayout from "../../Layouts/ComponentLayout.tsx";
import AssignedRolesSelector from "../../Roles/AssignedRolesSelector.tsx";
import AdUserDD from "../../Tools/AdUserDD.tsx";
import TooltipButton from "../../Tools/TooltipButton.tsx";
import { UserRequestRole, userReuest } from "./_core/_models.ts";
import { UpsertUserRequest } from "./_core/_request.ts";
import DimensionSection from "./DimensionSection.tsx";

const dimHeaders = {
    "D_RB_PROD": {
        title: "Product Dimension",
        header: "Product Line Dimension",
        subHeader: "Product Lines Needed:",
        checkLabel: "All",
        ddNote: "If not ALL, please use the drop down boxes below to specify specific product line role(s) needed.",
        textNote: "If the product line(s) you need do not yet exist in the list boxes above, please specify the product line description in one of the numbered boxes provided below so it can be built. If it doesn't exist in either group of drop-down boxes, please type in the product line number and description in the lines below.",
    },
    "D_RB_CUST": {
        title: "Customer Dimension",
        header: "Customer Dimension",
        subHeader: "Customers Needed:",
        checkLabel: "All",
        ddNote: "If not ALL, please specify specific customer(s) below.",
        textNote: "If the Customer(s) you need do not yet exist in the list boxes above, please specify the Customers description in one of the numbered boxes provided below so it can be built. If it doesn't exist in either group of drop-down boxes, please type in the customer and description in the lines below.",
    },
    "D_RB_BU_SBU": {
        title: "SBU Dimension",
        header: "SBU (Order Management SBU) Dimension",
        subHeader: "SBUs Needed:",
        checkLabel: "All",
        ddNote: "If not ALL, please specify the SBU(s) below.",
        textNote: "If the SBU(s) you need do not yet exist in the list boxes above, please specify the SBU description in one of the numbered boxes provided below so it can be built. If it doesn't exist in either group of drop-down boxes, please type in the SBU and description in the lines below.",
    },
    "D_RB_OMBU": {
        title: "BU Dimension",
        header: "BU (Order Management BU) Dimension",
        subHeader: "BUs Needed:",
        checkLabel: "All",
        ddNote: "If not ALL, please specify BU(s) below.",
        textNote: "If the BU(s) you need do not yet exist in the list boxes above, please specify the BU description in one of the numbered boxes provided below so it can be built. If it doesn't exist in either group of drop-down boxes, please type in the BU and description in the lines below.",
    },
    "D_RB_SOG": {
        title: "SOG Dimension",
        header: "Sales Organization Geography (SOG) Dimension",
        subHeader: "SOGs Needed:",
        checkLabel: "All",
        ddNote: "If not ALL, please specify specific SOG(s) below. If it does not exist in the drop down list please provide information as shown on the SOG LISTING tab.",
        textNote: "If the SOG(s) you need do not yet exist in the list boxes above, please specify the SOG description in one of the numbered boxes provided below so it can be built. If it doesn't exist in either group of drop-down boxes, please type in the SOG and description in the lines below.",
    }
};

export function UserRequest() {
    const [formData, setFormData] = useState<userReuest>();
    const [activeTab, setActiveTab] = useState("details");
    const [allowedTabs, setAllowedTabs] = useState(["details", "roles"]);
    const [manualMode, setManualMode] = useState(false);
    const [progress, setProgress] = useState<APIState>({ progress: false, success: false, message: "" });
    const navigate = useNavigate();
    const { roles, dimensions, setAllUsers, allUsers } = useAppContext();
    const { user } = useContext(AuthContext);

    useEffect(() => {
        const initVals: userReuest = {
            stagingUserID: 0,
            managerUserID: user.userID,
            managerExt: "",
            userFirstName: "",
            userName: "",
            userLastName: "",
            email: "",
            role: "",
            isAdUser: 0,
            userExt: "",
            boNeeded: false,
            requestDate: new Date().toLocaleDateString(),
            replacingFormerUser: false,
            formerUserID: 0,
            status: "requested",
            statusComment: "",
            applicationAccess: 0,
            bOLicenceUserID: 0,
            internationalOnly: 0,
            customerDimension: 0,
            dimesionsRoles: {},
            roleGroups: "",
            sections: { details: true },
            userRequestedBy: user.userID,
            userApprovedBy: 0,
            replaceRoles: false,
            dirtyFields: [],
            userID: 0
        };
        for (const indx in dimensions) {
            const curDim = dimensions[indx];
            initVals.dimesionsRoles[curDim.dimensionCode] = [];
            initVals.sections[curDim.dimensionCode] = false;
        }
        setFormData(initVals);
    }, []);

    const handleChange = (e) => {
        if (typeof formData != 'undefined') {
            const { name, value, type, checked } = e.target;
            const prev = { ...formData };
            prev[name] = type === "checkbox" ? checked : value;
            setFormData(prev);
        }
    };

    const saveRecords = () => {
        setProgress({ message: "Saving...", progress: true, success: false });
        if (typeof formData != 'undefined') {
            let fData = JSON.parse(JSON.stringify(formData));
            let nRoleId = -1;
            for (var d in fData.dimesionsRoles) {
                for (var dIndx in fData.dimesionsRoles[d]) {
                    let tRole = fData.dimesionsRoles[d][dIndx];
                    if (tRole.roleID == null || tRole.roleID <= 0) {
                        fData.dimesionsRoles[d][dIndx].roleID = nRoleId;
                        nRoleId--;
                    }
                }
            }
            fData.dirtyFields = [
                "stagingUserID",
                "managerUserID",
                //"managerExt",
                "isAdUser",
                "userFirstName",
                "userName",
                "userLastName",
                "email",
                "role",
                //"userExt",
                //"boNeeded",
                "requestDate",
                //"replacingFormerUser",
                //"formerUserID",
                //"status",
                //"statusComment",
                //"applicationAccess",
                //"bOLicenceUserID",
                //"internationalOnly",
                //"customerDimension",
                "dimesionsRoles",
                "roleGroups",
                //"sections",
                "userRequestedBy",
                //"userApprovedBy",
                "replaceRoles"
            ];
            UpsertUserRequest(fData).then(() => {
                setProgress({ message: "Saved successfully", progress: true, success: true });
                GetUsers({ userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null }).then((x) => {
                    setAllUsers([...x.data]);
                    navigate("/requests");
                })
            });
        }
    };

    const saveRecordsWithForm = (form: userReuest) => {
        setProgress({ message: "Saving...", progress: true, success: false });
        if (typeof form != 'undefined') {
            form.dirtyFields = [
                "stagingUserID",
                "managerUserID",
                //"managerExt",
                "isAdUser",
                "userFirstName",
                "userName",
                "userLastName",
                "email",
                "role",
                //"userExt",
                //"boNeeded",
                "requestDate",
                //"replacingFormerUser",
                //"formerUserID",
                //"status",
                //"statusComment",
                //"applicationAccess",
                //"bOLicenceUserID",
                //"internationalOnly",
                //"customerDimension",
                "dimesionsRoles",
                "roleGroups",
                //"sections",
                "userRequestedBy",
                //"userApprovedBy",
                "replaceRoles"
            ];
            UpsertUserRequest(form).then(() => {
                setProgress({ message: "Saved successfully", progress: true, success: true });
                GetUsers({ userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null }).then((x) => {
                    setAllUsers([...x.data]);
                    navigate("/requests");
                })
            });
        }
    };

    const AreDimensionSelectionProper = (dRoles: UserRequestRole[]) => {
        let yes = false;
        yes = (dRoles?.length > 0 &&
            dRoles?.findIndex((r) => r.roleID == null || (r.roleID <= 0 && (r.conditions.isEmpty() || r.conditions == "0" || r.conditions.split(String.fromCharCode(180))[2]?.isEmpty()))) == -1);
        return yes;
    }

    const isFormNotFilled = () => {
        if (!formData) return true;
        if (formData.userName.isEmpty() || formData.managerUserID <= 0 || formData.email.isEmpty())
            return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return true;;
        }
        return false;
    };

    const getMyError = (fld: string) => {
        let error = "";
        if (typeof formData != 'undefined') {
            switch (fld) {
                case "userName":
                    if (formData.userName.isEmpty())
                        error = "Fill User Name";
                    break;
                case "managerUserID":
                    if (formData.managerUserID <= 0)
                        error = "Fill Manager/Requested by";
                    break;
                case "email":
                    if (formData.email.isEmpty())
                        error = "Fill User's Email";
                    else {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(formData.email)) {
                            error = "Enter a valid email";
                        }
                    }
                    break;
            }
        }
        else {
            switch (fld) {
                case "userName":
                    error = "Fill User Name";
                    break;
            }
        }

        return error;
    }

    const isSubmitDisabled = () => {
        if (!formData) return true;
        if (formData.userName.isEmpty() || formData.managerUserID <= 0)
            return true;

        return !Object.keys(formData.dimesionsRoles).every((key) => (AreDimensionSelectionProper(formData.dimesionsRoles[key])));
    };

    const getValidationClass = (fieldValue) => {
        return !fieldValue ? "border border-danger" : "";
    };

    const selectedRoleIds = () => {
        let rIds: string[] = [];
        if (typeof formData != 'undefined') {
            for (var d in formData.dimesionsRoles) {
                let r = formData.dimesionsRoles[d];
                for (var indx in r) {
                    let roleId = r[indx].roleID;
                    if (roleId != null)
                        rIds.push(roleId.toString());
                }
            }
        }
        return rIds.join(",");
    }

    return (<ComponentLayout bodyHeight={0} exButtons={undefined} objectType={AuditObjectTypes.UserRequest} subHeader={undefined} title={<span className="d-flex flex-row align-items-center gap-2">
        <div className='fw-normal'>
            User Security Request
        </div>
        <span className='fs-3 px-2 mx-2 fw-normal' style={{ opacity: "0.2" }}>|</span>
        <button className='btn px-0' onClick={() => {
            navigate("/requests")
        }}>
            <div className='d-flex flex-row align-items-center gap-1 fw-500 j_req_bkbtn'>
                <PiArrowBendUpLeft size={16} />
                Back to all Requests
            </div>
        </button>
    </span>}
        fixedLayout={true} instanceId={0}
        cBody={
            !formData ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                    <Spinner animation="border" /> <span className="ms-2">Loading...</span>
                </div>
            ) : (
                <div className="px-4 py-2" style={{ maxHeight: "100%", overflow: "auto" }}>
                    <div className="mb-4">
                        <h5 className="text-primary">Request Details</h5>
                        <div className="row mb-4 border rounded-3 p-3 mx-0">
                            <div className="col-md-6 mb-3 j_req_by">
                                <label className="form-label fw-bold">Requested by:<MandatoryStar /></label>
                                <div className="form-control bg-card">
                                    {
                                        allUsers.find((x) => x.userID == formData.managerUserID)?.userName
                                    }
                                    {/* <UsersDD placeholder="Select manager" initSelect={formData.managerUserID.toString()} applicationAccess={[UserType.Admin, UserType.Manager]} updateUser={(u) => {
                                            const prev = { ...formData };
                                            prev.managerUserID = u.userID;
                                            prev.managerExt = (Math.floor(4000 + Math.random() * 9000)).toString();
                                            setFormData(prev);
                                        }} /> */}
                                </div>
                            </div>
                            <div className="col-md-6 mb-3 j_req_date">
                                <label className="form-label fw-bold">Request Date:</label>
                                <div className="form-control bg-gray">{formData.requestDate}</div>
                            </div>
                            {/* <div className="col-md-6 mb-3 d-flex align-items-end">
                                    <div className="input-group">
                                        <span className="input-group-text">Ext</span>
                                        <input
                                            type="text"
                                            name="managerExt"
                                            value={formData.managerExt}
                                            onChange={handleChange}
                                            className="form-control"
                                            style={{ maxWidth: '100px' }}
                                        />
                                    </div>
                                </div> */}

                            <div className="col-md-6 mb-3  j_req_for">
                                <div className="d-flex flex-row align-items-center justify-content-between w-100">
                                    <label className="form-label fw-bold">
                                        <span className="d-flex flex-row align-items-center gap-1">
                                            {
                                                (formData.isAdUser == 1) ?
                                                    <TiVendorMicrosoft size={16} color={defaultTheme.colors.primary} />
                                                    :
                                                    <FaUserCircle size={16} color={defaultTheme.colors.primary} />
                                            }
                                            {
                                                (formData.isAdUser == 1) ?
                                                    <>Requested for <br /> <span className="fw-normal">(Choose from Active Directory)</span>:<MandatoryStar /></> :
                                                    <>Requested for:<MandatoryStar /></>
                                            }

                                        </span>
                                    </label>
                                    {/* <TooltipButton title={formData.isAdUser == 1 ? "Disable this to type a user name manually" : "Enable this to select from Active Directory users"}>
                                    <div className="form-check form-switch j_req_ad_tgl">
                                            <input
                                                type="checkbox"
                                                role="switch"
                                                name="isAdUser"
                                                id="isAdUser"
                                                checked={formData.isAdUser == 1}
                                                onChange={(e) => {
                                                    let prev = formData;
                                                    prev.userName = "";
                                                    prev.email = "";
                                                    prev.userExt = "";
                                                    prev.isAdUser = e.target.checked ? 1 : 0;
                                                    setFormData({ ...prev });
                                                }}
                                                className="form-check-input form-switch"
                                            />
                                            <label className="form-check-label" htmlFor="isAdUser">
                                                AD User
                                            </label>
                                        </div>
                                    </TooltipButton> */}
                                </div>

                                <div className={`${getMyError("userName") != "" ? "is-invalid" : ""} mb-3`}>
                                    <AdUserDD
                                        placeholder="Select AD User"
                                        initSelect={formData.userName}
                                        avoidUser={[]}
                                        needLoginUser={false}
                                        updateUser={(u) => {
                                            const updated = { ...formData };
                                            if (u == null) {
                                                updated.userName = "";
                                                updated.email = "";
                                                updated.userExt = "";
                                                updated.userFirstName = "";
                                                updated.userLastName = "";
                                                updated.userID = 0;
                                            }
                                            else {
                                                updated.userName = u.userName;
                                                updated.email = u.userEmail;
                                                updated.userFirstName = u.firstName;
                                                updated.userLastName = u.lastName;
                                                updated.userExt = (Math.floor(4000 + Math.random() * 9000)).toString();
                                                updated.userID = u.userID;
                                            }
                                            setFormData(updated);
                                        }}
                                    />
                                    {
                                        <div className="invalid-feedback">
                                            {
                                                getMyError("userName")
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className="col-md-6 mb-3 j_req_mail">
                                <label className="form-label fw-bold">Email Address:<MandatoryStar /></label>
                                <input
                                    type="text"
                                    name="email"
                                    disabled
                                    placeholder="Enter Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`${getMyError("email") != "" ? "is-invalid" : ""} form-control`}
                                />
                                {
                                    <div className="invalid-feedback">
                                        {
                                            getMyError("email")
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Alert
                            variant="warning"
                            className="px-3 py-2"
                            style={{ borderLeft: "5px solid #ffc107", fontSize: "0.95rem" }}
                        >
                            <div className="d-flex align-items-center gap-2 flex-wrap j_req_mrg_tgl">
                                <MdError size={18} color="#ffc107" />
                                <span className="d-flex align-items-center gap-1 flex-wrap">
                                    Selected roles will
                                    <strong className="mx-1">{formData.replaceRoles ? "replace" : "add to"}</strong>
                                    existing roles.
                                    <span className="form-switch ms-1">
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            className="form-check-input form-switch py-2"
                                            name="replaceRoles"
                                            id="replaceRoles"
                                            checked={formData.replaceRoles}
                                            onChange={handleChange}
                                            style={{ borderColor: defaultTheme.colors.border }}
                                        />
                                    </span>
                                </span>
                            </div>

                            <div className="text-muted-theme small mt-2 ms-4">
                                {formData.replaceRoles
                                    ? "All existing roles assigned to this user will be removed before applying the selected ones."
                                    : "Selected roles will be merged with the user’s existing roles — without removing any."}
                            </div>
                        </Alert>
                    </div>
                    <div className="mb-4">
                        <h5 className="text-primary">Choose Role Assignment Method</h5>
                        <AssignedRolesSelector
                            options={[{ name: "group", title: LabelConstants.RoleGroups, selected: true }, { name: "user", title: "User", selected: false }
                                , { name: "manual", title: "Manual", selected: false }
                            ]}
                            typeLabel="Select Roles from"
                            helpLabel="and continue creating request same as selected."
                            allRoles={roles}
                            replaceOlds={true}
                            importButton={(disabled, onEvent) => {
                                return manualMode ? null : <div className="w-100 justify-content-end d-flex flex-row">
                                    <AnimatePresence>
                                        {progress.progress ? (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 10 }}
                                                style={{
                                                    position: "fixed",
                                                    bottom: 20,
                                                    left: 0,
                                                    right: 0,
                                                    margin: "0 auto",
                                                    zIndex: 1055,
                                                    width: "fit-content",
                                                    maxWidth: "90%",
                                                    pointerEvents: "none", // Optional: allows clicks to pass through
                                                }}
                                            >
                                                <Alert variant={progress.success ? "success" : "warning"} className="d-flex align-items-center gap-2 shadow-lg px-3 py-2 mb-0">
                                                    <SavingIndicator
                                                        {...{
                                                            color: defaultTheme.colors.primary,
                                                            style: "spinner",
                                                            message: progress.message,
                                                            type: "saving"
                                                        }}
                                                    />
                                                </Alert>
                                            </motion.div>
                                        ) :
                                            <Button className="j_req_sbmt" variant={(disabled || isFormNotFilled()) ? "secondary" : "primary"} disabled={disabled || isFormNotFilled()}
                                                onClick={() => {
                                                    onEvent((r, fValue) => {
                                                        if (r.length == 0) {

                                                        }
                                                        else {
                                                            let pVal = formData;
                                                            if (fValue.from.toLowerCase().indexOf("group") > -1) {
                                                                pVal.roleGroups = fValue.fromId.toString();
                                                            }
                                                            else
                                                                pVal.roleGroups = "";
                                                            for (var dIndx in pVal.dimesionsRoles) {
                                                                pVal.dimesionsRoles[dIndx] = [];
                                                            }
                                                            for (let i = 0; i < r.length; i++) {
                                                                let cRole = r[i];
                                                                if (pVal.dimesionsRoles[cRole.dataMapCode].findIndex((rl) => (rl.roleID == cRole.roleID)) == -1)
                                                                    pVal.dimesionsRoles[cRole.dataMapCode].push({
                                                                        conditions: "",
                                                                        roleID: cRole.roleID,
                                                                        stagingUserID: 0,
                                                                        excluded: false
                                                                    });
                                                            }
                                                            // if (pVal.importString != "") {
                                                            //     let arr = pVal.importString.split("__");
                                                            //     pVal.importString = `${r.length}__${arr[1] + ", " + from}`;
                                                            // }
                                                            // else
                                                            //     pVal.importString = `${r.length}__${from}`;
                                                            //setFormData({ ...pVal });
                                                            saveRecordsWithForm(pVal);
                                                        }
                                                    })
                                                }}>
                                                Submit
                                            </Button>}
                                    </AnimatePresence>
                                </div>
                            }}
                            onTypeChange={(t) => {
                                const isManual = t === "manual";
                                setManualMode(isManual);
                                if (isManual) {
                                    const dimensionKeys = Object.keys(dimHeaders);
                                    setAllowedTabs(["details", "roles", ...Object.keys(dimHeaders)]);
                                    setActiveTab(dimensionKeys[0]);
                                } else {
                                    setAllowedTabs(["details", "roles"]);
                                }
                                // Making it empty
                                let pVal = formData;
                                for (var dIndx in pVal.dimesionsRoles) {
                                    pVal.dimesionsRoles[dIndx] = [];
                                }
                                setFormData({ ...pVal });
                            }}
                            onChange={(r, from, fromId) => {
                                if (from === "manual") {
                                    // setAllowedTabs(["details", "roles", ...Object.keys(dimHeaders)]);

                                }
                                else {
                                    if (r.length == 0) {

                                    }
                                    else {
                                        let pVal = formData;
                                        if (from.toLowerCase().indexOf("group") > -1) {
                                            pVal.roleGroups = fromId;
                                        }
                                        else
                                            pVal.roleGroups = "";
                                        for (var dIndx in pVal.dimesionsRoles) {
                                            pVal.dimesionsRoles[dIndx] = [];
                                        }
                                        for (let i = 0; i < r.length; i++) {
                                            let cRole = r[i];
                                            if (pVal.dimesionsRoles[cRole.dataMapCode].findIndex((rl) => (rl.roleID == cRole.roleID)) == -1)
                                                pVal.dimesionsRoles[cRole.dataMapCode].push({
                                                    conditions: "",
                                                    roleID: cRole.roleID,
                                                    stagingUserID: 0,
                                                    excluded: false
                                                });
                                        }
                                        // if (pVal.importString != "") {
                                        //     let arr = pVal.importString.split("__");
                                        //     pVal.importString = `${r.length}__${arr[1] + ", " + from}`;
                                        // }
                                        // else
                                        //     pVal.importString = `${r.length}__${from}`;
                                        setFormData({ ...pVal });
                                    }
                                }
                            }}
                            exUI={
                                allowedTabs.length <= 2 ?
                                    <div className="w-100">
                                        {/* <span className="text-muted">Selected Roles:</span>
                                            <GroupExpandView roleIDS={selectedRoleIds()} tClass="px-0 py-1"
                                                emptyUI={<div>
                                                    <div
                                                        className="text-center py-4"
                                                    >
                                                        <div className="d-flex justify-content-center align-items-center mb-3">
                                                            <FiUserX size={48} color="#6c757d" />
                                                        </div>
                                                        <h5 style={{ color: "#6c757d", fontWeight: 500 }}>No roles selected</h5>
                                                        <p className="text-muted" style={{ fontSize: "0.95rem" }}>
                                                            You can copy role(s) from a User or select a Group.
                                                        </p>
                                                    </div>
                                                </div>} /> */}
                                    </div> :
                                    <div className="row g-0 px-2">
                                        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "details")}
                                            className="mb-0 custom-tabs border rounded-3  r_man_tabs" variant="pills" justify>
                                            {allowedTabs.slice(2).map((dimKey) => (
                                                <Tab className="r_man_tabs" eventKey={dimKey}
                                                    title={<div className="d-inline-flex align-items-center" style={{ gap: "0.3rem", lineHeight: 1, }}>
                                                        <span style={{ display: "inline-block", verticalAlign: "middle" }}>{dimHeaders[dimKey].title}</span>
                                                        <span style={{ display: "inline-flex", alignItems: "center" }}>
                                                            {AreDimensionSelectionProper(formData.dimesionsRoles[dimKey]) ? (
                                                                <TooltipButton title={"Required Details filled"}>
                                                                    <TbChecks size={16} style={{ verticalAlign: "middle" }} />
                                                                </TooltipButton>
                                                            ) : (
                                                                <TooltipButton title={"Details not filled"}>
                                                                    <MdError color="red" size={16} style={{ verticalAlign: "middle" }} />
                                                                </TooltipButton>
                                                            )}
                                                        </span>
                                                    </div>} key={dimKey}>
                                                    <div className="custom-tab-content mt-2">
                                                        <DimensionSection
                                                            onExpand={() => { }}
                                                            dimensionCode={dimKey}
                                                            expand={true}
                                                            header={dimHeaders[dimKey].header}
                                                            subHeader={dimHeaders[dimKey].subHeader}
                                                            checkLabel={dimHeaders[dimKey].checkLabel}
                                                            ddNote={dimHeaders[dimKey].ddNote}
                                                            textNote={dimHeaders[dimKey].textNote}
                                                            roleItems={formData.dimesionsRoles[dimKey]}
                                                            updateItems={(items) => {
                                                                const updated = { ...formData };
                                                                updated.dimesionsRoles[dimKey] = items;
                                                                setFormData(updated);
                                                            }}
                                                        />
                                                    </div>
                                                </Tab>
                                            ))}
                                        </Tabs>
                                    </div>
                            }
                        />
                    </div>
                    {
                        allowedTabs.length > 2 &&
                        <AnimatePresence>
                            {progress.progress ? (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                    style={{
                                        position: "fixed",
                                        bottom: 20,
                                        left: 0,
                                        right: 0,
                                        margin: "0 auto",
                                        zIndex: 1055,
                                        width: "fit-content",
                                        maxWidth: "90%",
                                        pointerEvents: "none", // Optional: allows clicks to pass through
                                    }}>
                                    <Alert variant={progress.success ? "success" : "warning"} className="mt-3 d-flex align-items-center gap-2">
                                        <SavingIndicator {...{ color: defaultTheme.colors.primary, style: "spinner", message: progress.message, type: "saving" }} />
                                    </Alert>
                                </motion.div>
                            ) :
                                <div className="text-end mt-4 j_req_sbmt">
                                    <Button variant={isSubmitDisabled() ? "secondary" : "primary"} disabled={isSubmitDisabled()} onClick={saveRecords}>
                                        Submit
                                    </Button>
                                </div>}
                        </AnimatePresence>
                    }
                </div>)
        } />
    );
}

export default UserRequest;